import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { LocaleService } from '@next/core-lib/i18n';
import { Observable } from 'rxjs';

import {
  FmsDiagnoseAddress,
  FmsDiagnoseProvider,
  FmsDiagnoseProviderCustomer,
  FmsDiagnoseProviderLink,
  FmsDiagnoseResponse,
} from '../../../shared/models/fms-diagnose.model';

export type FmsDiagnoseSummaryState =
  | { kind: 'loading' }
  | { kind: 'loaded'; data: FmsDiagnoseResponse }
  | { kind: 'error' };

export type FmsDiagnoseSearchState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'loaded'; customers: FmsDiagnoseProviderCustomer[] }
  | { kind: 'empty' }
  | { kind: 'error' };

export type FmsDiagnoseSearchForm = FormGroup<{
  provider: FormControl<FmsDiagnoseProvider | null>;
  year: FormControl<number>;
  customerName: FormControl<string>;
}>;

interface FmsDiagnoseKvkGroup {
  key: string;
  kvkNumber: string;
  links: FmsDiagnoseProviderLink[];
}

interface FmsDiagnoseProviderGroup {
  provider: FmsDiagnoseProvider;
  kvkGroups: FmsDiagnoseKvkGroup[];
}

@Component({
  selector: 'app-fms-diagnose-linkage-tab',
  templateUrl: './fms-diagnose-linkage-tab.component.html',
  styleUrls: ['./fms-diagnose-linkage-tab.component.scss'],
  standalone: false,
})
export class FmsDiagnoseLinkageTabComponent {
  @Input({ required: true }) summaryState$!: Observable<FmsDiagnoseSummaryState>;
  @Input({ required: true }) searchState$!: Observable<FmsDiagnoseSearchState>;
  @Input({ required: true }) searchForm!: FmsDiagnoseSearchForm;
  @Input({ required: true }) years!: number[];
  @Input({ required: true }) filteredCustomers$!: Observable<FmsDiagnoseProviderCustomer[]>;

  @Output() readonly retryRequested = new EventEmitter<void>();
  @Output() readonly retrySearchRequested = new EventEmitter<void>();

  private readonly locale = inject(LocaleService);
  readonly providerOrder: FmsDiagnoseProvider[] = ['Dacom', 'Agrovision'];

  formatAddress(address: FmsDiagnoseAddress): string {
    const street = [address.street, address.houseNumber, address.houseNumberAddition]
      .filter(Boolean)
      .join(' ');
    const city = [address.postalCode, address.city].filter(Boolean).join(' ');
    return [street, city].filter(Boolean).join(', ');
  }

  unavailable(): string {
    return this.locale.translate('fmsDiagnose.shared.unavailable');
  }

  providerCustomerName(customer: FmsDiagnoseProviderCustomer): string {
    return (
      customer.customerName.trim() ||
      this.locale.translate('fmsDiagnose.tabs.linkage.farmNameUnavailable')
    );
  }

  locationLabel(count: number): string {
    return this.locale.translate(
      count === 1 ? 'fmsDiagnose.tabs.linkage.location' : 'fmsDiagnose.tabs.linkage.locations',
    );
  }

  kvkLabel(count: number): string {
    return this.locale.translate(
      count === 1 ? 'fmsDiagnose.tabs.linkage.kvk' : 'fmsDiagnose.tabs.linkage.kvks',
    );
  }

  externalNamesAndFarmIdsLabel(count: number): string {
    return this.locale.translate(
      count === 1
        ? 'fmsDiagnose.tabs.linkage.externalNameAndFarmId'
        : 'fmsDiagnose.tabs.linkage.externalNamesAndFarmIds',
    );
  }

  hasMatch(data: FmsDiagnoseResponse): boolean {
    return data.hasMatch;
  }

  matchTitle(data: FmsDiagnoseResponse): string {
    let translationKey = 'fmsDiagnose.tabs.linkage.noStoredMatch';
    if (data.hasMatch) {
      translationKey =
        data.matchCount === 1
          ? 'fmsDiagnose.tabs.linkage.storedMatchOne'
          : 'fmsDiagnose.tabs.linkage.storedMatchMany';
    }

    return this.locale.translate(translationKey);
  }

  matchDescription(data: FmsDiagnoseResponse): string {
    return this.locale.translate(
      data.hasMatch
        ? 'fmsDiagnose.tabs.linkage.storedMatchDescription'
        : 'fmsDiagnose.tabs.linkage.noStoredMatchDescription',
    );
  }

  groupProviderLinks(links: FmsDiagnoseProviderLink[]): FmsDiagnoseProviderGroup[] {
    return this.providerOrder.flatMap((provider) => {
      const providerLinks = links.filter((link) => link.provider === provider);
      if (providerLinks.length === 0) {
        return [];
      }

      const groups = new Map<string, FmsDiagnoseKvkGroup>();
      providerLinks.forEach((link) => {
        const kvkNumber = link.kvkNumber.trim();
        const key = kvkNumber || '__unavailable__';
        let group = groups.get(key);
        if (!group) {
          group = { key, kvkNumber, links: [] };
          groups.set(key, group);
        }

        group.links.push(link);
      });

      return [{ provider, kvkGroups: Array.from(groups.values()) }];
    });
  }

  requestRetry(): void {
    this.retryRequested.emit();
  }

  requestSearchRetry(): void {
    this.retrySearchRequested.emit();
  }
}
